import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { filter, Subject, takeUntil, take } from 'rxjs';

import { ChatRole } from '@app/enums';
import { ChatMessage } from '@app/interfaces';
import { StreamingLeftNavigationService } from '../navigation/left-navigation.service';
import { ActiveConversationService } from './active-conversation.service';
import { RealtimeSessionStreamService } from '../realtime-session-stream/realtime-session-stream.service';
import { ConversationMessage } from '../realtime-session-stream/conversation-message.interface';
import { ConversationPersistenceService } from '../conversation-persistence/conversation-persistence.service';

@Component({
    selector: 'craig-streaming-active-conversation',
    templateUrl: './active-conversation.component.html',
    styleUrl: './active-conversation.component.less',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ActiveConversationComponent implements OnInit, OnDestroy {
    activeConversationMessages: ConversationMessage[] = [];
    isStreaming: boolean = false;
    showScrollToBottomButton: boolean = false;

    @ViewChild('activeConversationView') activeConversationContainer!: ElementRef;

    private readonly componentDestroyed$: Subject<boolean> = new Subject();
    private isUserScrolledUp: boolean = false;
    private isInitialLoad: boolean = true;
    private lastMessageCount: number = 0;
    private userHasControlledScroll: boolean = false;
    private shouldAutoScroll: boolean = true;

    constructor(
        private readonly activeConversationService: ActiveConversationService,
        private readonly leftNavigationService: StreamingLeftNavigationService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly _window: Window,
        private readonly realtimeStreamService: RealtimeSessionStreamService,
        private readonly conversationPersistence: ConversationPersistenceService
    ) {}

    ngOnInit(): void {
        // Setup scroll event listener to detect user scrolling
        this.setupScrollListener();

        // Subscribe to realtime conversation for all message updates
        this.realtimeStreamService.conversation$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((conversationState) => {
                const currentMessageCount = conversationState.messages.length;
                const isNewContent = !this.isInitialLoad && currentMessageCount > this.lastMessageCount;

                // Update active messages with the full conversation state
                this.activeConversationMessages = [...conversationState.messages];
                this.changeDetectorRef.detectChanges();

                // SIMPLE AUTO-SCROLL: Scroll to bottom during streaming unless user took control
                if (this.isStreaming && this.shouldAutoScroll) {
                    setTimeout(() => {
                        if (this.activeConversationContainer?.nativeElement) {
                            this.activeConversationContainer.nativeElement.scrollTop =
                                this.activeConversationContainer.nativeElement.scrollHeight;
                        }
                    }, 10);
                }

                // Update tracking vars
                this.lastMessageCount = currentMessageCount;
                this.isInitialLoad = false;

                // Check if we need to show scroll-to-bottom button
                setTimeout(() => this.checkScrollPosition(), 100);
            });

        this.leftNavigationService
            .chatShowedChange()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.clearActiveConversation();
                // Reset tracking for anti-scroll-jacking
                this.isInitialLoad = true;
                this.lastMessageCount = 0;

                this.changeDetectorRef.detectChanges();
            });

        this.leftNavigationService
            .chatSessionSelectedChange()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((sessionId: number | null) => {
                // console.log('🔍 [ActiveConversationComponent] Received conversationSessionSelectedChange:', sessionId);
                if (sessionId !== null) {
                    // Reset tracking for anti-scroll-jacking on session change
                    this.isInitialLoad = true;
                    this.lastMessageCount = 0;

                    // CRITICAL: Set the current session in unified persistence service
                    this.conversationPersistence.addNewConversation(sessionId);
                    // console.log('🎯 Set current session ID to:', sessionId);

                    // CRITICAL FIX: Use ConversationPersistenceService to load with cognitive states
                    let messages = this.conversationPersistence.loadConversation(sessionId);

                    // console.log(
                    //     '🔍 [ActiveConversationComponent] Loading messages for session:',
                    //     sessionId,
                    //     'count:',
                    //     messages.length
                    // );

                    // Load the conversation into realtime stream service (single source of truth)
                    // Pass sessionId to ensure we load the correct cognitive state
                    this.realtimeStreamService.loadConversationFromStorage(messages, sessionId);
                } else {
                    // CRITICAL FIX: Only reset if we don't have an active conversation
                    // This prevents follow-up responses from losing cognitive events
                    this.realtimeStreamService.conversation$.pipe(take(1)).subscribe((state) => {
                        if (state.messages.length === 0) {
                            // console.log('🔍 [ActiveConversationComponent] Session is null and no active conversation, resetting');
                            this.realtimeStreamService.resetConversation();
                        } else {
                            // console.log('🛡️ [ActiveConversationComponent] Session is null but conversation active, preserving state');
                        }
                    });
                }
            });

        // Subscribe to streaming state
        this.realtimeStreamService.isStreaming$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((streaming: boolean) => {
                const wasStreaming = this.isStreaming;
                this.isStreaming = streaming;

                // When streaming starts, reset control and scroll to bottom
                if (!wasStreaming && streaming) {
                    // console.log('🚀 STREAMING STARTED');
                    this.shouldAutoScroll = true; // Reset control for new stream
                    this.scrollToBottomImmediately();
                } else if (wasStreaming && !streaming) {
                    // console.log('🛑 STREAMING ENDED');
                }

                this.changeDetectorRef.detectChanges();
            });
    }

    ngOnDestroy(): void {
        this.componentDestroyed$.next(true);
    }

    clearActiveConversation(): void {
        if (this.activeConversationMessages.length) {
            this.activeConversationMessages = [];
        }
        // Reset tracking
        this.isInitialLoad = true;
        this.lastMessageCount = 0;
        this.shouldAutoScroll = true;
        this.userHasControlledScroll = false;
        this.isUserScrolledUp = false;
        this.showScrollToBottomButton = false;
    }

    isAssistantMessage(messageRole: ChatRole): boolean {
        return messageRole === ChatRole.Assistant;
    }

    isFeedbackPromptMessage(message: ConversationMessage): boolean {
        return message.messageType === 'feedback_prompt';
    }

    private scrollToBottom(): void {
        if (!this.activeConversationContainer?.nativeElement) return;

        this._window.setTimeout(() => {
            const element = this.activeConversationContainer.nativeElement;
            element.scrollTop = element.scrollHeight;
            this.isUserScrolledUp = false;
            this.showScrollToBottomButton = false;
            this.changeDetectorRef.markForCheck();
        });
    }

    private scrollToBottomImmediately(): void {
        if (!this.activeConversationContainer?.nativeElement) return;

        const element = this.activeConversationContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
        // console.log('📜 SCROLLED TO BOTTOM:', element.scrollTop, 'of', element.scrollHeight);
    }

    private setupScrollListener(): void {
        this._window.setTimeout(() => {
            if (this.activeConversationContainer?.nativeElement) {
                const element = this.activeConversationContainer.nativeElement;

                // Initialize scroll state to bottom (assume user starts at bottom)
                this.isUserScrolledUp = false;

                // Listen for scroll events (for button visibility)
                element.addEventListener('scroll', () => {
                    this.checkScrollPosition();
                });

                // Listen for wheel events - user wants control
                element.addEventListener(
                    'wheel',
                    () => {
                        if (this.isStreaming && this.shouldAutoScroll) {
                            // console.log('🖱️ USER WANTS CONTROL - Disabling auto-scroll');
                            this.shouldAutoScroll = false;
                        }
                    },
                    { passive: true }
                );

                // Listen for touch events - user wants control
                element.addEventListener(
                    'touchstart',
                    () => {
                        if (this.isStreaming && this.shouldAutoScroll) {
                            // console.log('👆 USER WANTS CONTROL - Disabling auto-scroll');
                            this.shouldAutoScroll = false;
                        }
                    },
                    { passive: true }
                );
            }
        }, 100);
    }

    private checkScrollPosition(): void {
        if (!this.activeConversationContainer?.nativeElement) return;

        const element = this.activeConversationContainer.nativeElement;
        const threshold = 100;
        const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - threshold;

        this.isUserScrolledUp = !isAtBottom;
        this.showScrollToBottomButton = this.isUserScrolledUp && element.scrollHeight > element.clientHeight;
        this.changeDetectorRef.markForCheck();
    }

    scrollToBottomClicked(): void {
        // Immediately hide the button to prevent flickering during scroll
        this.showScrollToBottomButton = false;
        this.isUserScrolledUp = false;
        this.shouldAutoScroll = true;
        this.changeDetectorRef.detectChanges(); // Force immediate update

        // Scroll immediately without setTimeout
        this.scrollToBottomImmediately();
    }
}
